import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { Box, Edges } from "@react-three/drei";
import { useRoomStore, useUserInterfaceStore } from "../../store";
import { materialOptions } from "../../constants/materialOptions.js";
import * as THREE from "three";
import VideoMaterial from "./VideoMaterial";
import ImageWallMaterial from "./ImageWallMaterial";

function RoomGeometry({ boxRef, customProps = {}, roomLining }) {
  const scaleX = useRoomStore((s) => s.scaleX);
  const scaleZ = useRoomStore((s) => s.scaleZ);
  const height = useRoomStore((s) => s.height);
  const materialType = useRoomStore((s) => s.materialType);
  const materialId = useRoomStore((s) => s.materialId);
  const selectedMaterialOptions = useRoomStore((s) => s.materialOptions);
  const shaderOptions = useRoomStore((s) => s.shaderOptions);
  const playerMode = useUserInterfaceStore((s) => s.playerMode);
  const showRoomLining = useUserInterfaceStore((s) => s.showRoomLining);
  const roomLiningVisible = useRoomStore((s) => s.roomLiningVisible);

  const isEditing = playerMode === "editor";

  // Show room lining: in editor mode use showRoomLining, in player mode use roomLiningVisible
  const shouldShowLining = isEditing ? showRoomLining : roomLiningVisible;

  const option = materialOptions[materialId];

  // Shader setup
  const shaderRef = useRef();
  useFrame((state, delta) => {
    if (shaderRef.current) {
      shaderRef.current.uniforms.uTime.value += delta;
    }
  });
  const materialSide = isEditing ? THREE.BackSide : THREE.DoubleSide;
  const shaderMaterial = useMemo(() => {
    if (option?.type === "shader" && option.shader) {
      // Clone uniforms and apply any overrides from shaderOptions
      const uniforms = {};
      for (const [key, uniform] of Object.entries(option.shader.uniforms)) {
        if (shaderOptions[key] && uniform.value instanceof THREE.Color) {
          // Apply color override
          uniforms[key] = { value: new THREE.Color(shaderOptions[key]) };
        } else {
          // Clone the default uniform
          uniforms[key] = { value: uniform.value instanceof THREE.Color
            ? uniform.value.clone()
            : uniform.value };
        }
      }
      // Apply speed multiplier
      if (shaderOptions.speedMultiplier !== undefined) {
        const defaultSpeed = option.shader.uniforms.uSpeed?.value ?? 0.2;
        uniforms.uSpeed = { value: defaultSpeed * shaderOptions.speedMultiplier };
      }
      // In editor mode, scale world position to match player mode (which has 0.125 scale)
      if (uniforms.uWorldScale && isEditing) {
        uniforms.uWorldScale = { value: 0.125 };
      }
      return new THREE.ShaderMaterial({
        uniforms,
        vertexShader: option.shader.vertexShader,
        fragmentShader: option.shader.fragmentShader,
        side: materialSide,
      });
    }
    return null;
  }, [option, materialSide, shaderOptions, isEditing]);

  return (
    <group>
      <Box
        {...customProps}
        ref={boxRef}
        position={[0, height / 2 - 0.1, 0]}
        args={[scaleX, height, scaleZ]}
      >
        {materialType === "basic" && (
          <meshPhysicalMaterial
            attach="material"
            side={materialSide}
            {...(option?.materialProps || {})}
            {...selectedMaterialOptions}
          />
        )}
        {materialType === "video" && (selectedMaterialOptions?.src || option?.src) && (
          <VideoMaterial side={materialSide} src={selectedMaterialOptions?.src || option?.src} />
        )}
        {materialType === "image" && (selectedMaterialOptions?.src || option?.src) && (
          <ImageWallMaterial side={materialSide} src={selectedMaterialOptions?.src || option?.src} />
        )}
        {materialType === "shader" && shaderMaterial && (
          <primitive
            object={shaderMaterial}
            attach="material"
            ref={shaderRef}
          />
        )}
        {shouldShowLining && (
          <Edges scale={0.99} color="white" lineWidth={roomLining || 10} />
        )}
      </Box>
    </group>
  );
}

export default RoomGeometry;
